// Libraries
import { formatISO, subDays, getTime } from "date-fns";

export const getSeconds = (d: string) => {
  return getTime(new Date(d)) / 1000;
};

export const timeSince = (timeStamp: number) => {
  if (!timeStamp || isNaN(timeStamp) || timeStamp < 1) {
    return " agora";
  } else if (timeStamp < 60) {
    const minutes = Math.round(timeStamp);
    return " há " + minutes + ` ${minutes == 1 ? "minuto" : "minutos"}`;
  } else if (timeStamp < 1440) {
    const hours = Math.round(timeStamp / 60);
    return " há " + hours + ` ${hours == 1 ? "hora" : "horas"}`;
  } else {
    const days = Math.round(timeStamp / 1440);
    return " há " + days + ` ${days == 1 ? "dia" : "dias"}`;
  }
};

export const currentTimeDiff = (givenTimeInSeconds: number) => {
  const currentTime = Math.floor(Date.now() / 1000);
  const difference = (currentTime - givenTimeInSeconds) / 60;
  return difference;
};

export const calculateDifference = (givenTimeInSeconds: number) => {
  const difference = currentTimeDiff(givenTimeInSeconds);
  return timeSince(difference);
};

export const sleep = async (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export const millisToMinutes = (millis: number): number => Math.floor(millis / 60000);

export class PausableTimeout {
  private timeoutId: NodeJS.Timeout | null = null;
  private start: number;
  private remaining: number;

  constructor(
    private callback: () => void,
    delay: number
  ) {
    this.remaining = delay;
    this.start = new Date().getTime();
    this.resume();
  }

  pause() {
    if (!this.timeoutId) return;

    clearTimeout(this.timeoutId);
    this.remaining -= new Date().getTime() - this.start;
    this.timeoutId = null;
  }

  resume() {
    if (this.timeoutId) return;

    this.start = new Date().getTime();
    this.timeoutId = setTimeout(this.callback, this.remaining);
  }

  clear() {
    if (!this.timeoutId) return;

    clearTimeout(this.timeoutId);
    this.timeoutId = null;
  }
}

export const daysDiff = (date1: string, date2: string) => {
  const date1Obj = new Date(date1);
  const date2Obj = new Date(date2);

  const diffTime = Math.abs(date2Obj.getTime() - date1Obj.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

export const millisToMinutesAndSeconds = (millis: number) => {
  const minutes = Math.floor(millis / 60000);
  const seconds = parseInt(((millis % 60000) / 1000).toFixed(0));

  return minutes + ":" + (seconds < 10 ? "0" : "") + seconds;
};

export const formatDate = (date?: Date | string, isDash?: boolean, withTime?: boolean): string | undefined => {
  if (!date) return;

  const parsedDate = typeof date === "string" ? new Date(date) : date;

  if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) {
    return undefined;
  }

  const day = parsedDate.getDate().toString().padStart(2, "0");
  const month = (parsedDate.getMonth() + 1).toString().padStart(2, "0");
  const year = parsedDate.getFullYear();

  let formattedDate = isDash ? `${year}-${month}-${day}` : `${day}/${month}/${year}`;

  if (withTime) {
    const hours = parsedDate.getHours().toString().padStart(2, "0");
    const minutes = parsedDate.getMinutes().toString().padStart(2, "0");
    const seconds = parsedDate.getSeconds().toString().padStart(2, "0");

    formattedDate += ` ${hours}:${minutes}:${seconds}`;
  }

  return formattedDate;
};

export const isExpired = (expirationDateParam: string) => {
  const expirationDate = new Date(expirationDateParam);
  const today = new Date();
  return expirationDate <= today;
};

export const minutesToHoursAndMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return {
    hours,
    minutes: remainingMinutes
  };
};

export const checkIfDateIsAfterToday = (date?: string | Date): boolean => {
  try {
    if (date) {
      let paramDate: Date;
      if (date instanceof Date) {
        paramDate = date;
      } else {
        paramDate = new Date(date);
      }
      if (isNaN(paramDate.getTime())) {
        return false;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return paramDate > today;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

export const getOneWeekAgo = () => {
  const today = new Date();
  const oneWeekAgo = subDays(today, 7);

  oneWeekAgo.setHours(0, 0, 0, 0);

  return formatISO(oneWeekAgo);
};

export const getTodayEnd = () => {
  const today = new Date();

  today.setHours(23, 59, 59, 0);

  return formatISO(today);
};

export const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getUTCDate() === date2.getUTCDate() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCFullYear() === date2.getUTCFullYear()
  );
};
